import { v4 as uuidv4 } from 'uuid';
import { getSignUrl, uploadFile } from 'api/upload';
import {
  ECardTypeActionType,
  ECardTypeTemplateId,
  TCardTypeAction,
  TCardTypeCard,
  TCardTypeImages,
  TCardTypeMessagePayload,
  TCardTypePrice,
  TCardTypeTag,
} from 'types/cardTypeMessage';
import type { TCardTypeImageMimeType } from './cardTypeMessageImage';
import { normalizeCardTypeActionUri } from './cardTypeMessageUri';
import { normalizeAdditionalInfoType } from '../constants/cardTypeMessageTemplates';
import {
  cardTypeDefaultAction,
  cardTypeDefaultAction2,
  cardTypeDefaultActions,
  cardTypeDefaultImages,
  cardTypeDefaultPrice,
  cardTypeDefaultPrice2,
  cardTypeDefaultTag,
  cardTypeTemplateDefaults,
} from './cardTypeMessageDefaults';

export function ensureCardActions(
  card: TCardTypeCard,
  count = 2,
): TCardTypeCard {
  const actions: TCardTypeAction[] = [...card.actions];
  while (actions.length < count) {
    actions.push(
      actions.length === 0
        ? cardTypeDefaultAction()
        : cardTypeDefaultAction2(),
    );
  }
  return { ...card, actions: actions.slice(0, count) };
}

export function getCardImages(card: TCardTypeCard): TCardTypeImages {
  const images = card.images ?? {};
  const a = images.a?.trim() || card.imageUrl?.trim() || '';
  return {
    a,
    b: images.b?.trim() || '',
    c: images.c?.trim() || '',
  };
}

export function syncCardPrimaryImage(card: TCardTypeCard): TCardTypeCard {
  const images = getCardImages(card);
  return {
    ...card,
    images,
    imageUrl: images.a || card.imageUrl,
  };
}

export function createDefaultCard(
  templateId: ECardTypeTemplateId,
  isClosingCard = false,
): TCardTypeCard {
  const tpl = cardTypeTemplateDefaults(templateId);
  const imageCount = tpl.imageCount ?? 1;
  const base: TCardTypeCard = {
    images: cardTypeDefaultImages(),
    imageCount,
    imageUrl: '',
    actions: cardTypeDefaultActions(),
    isClosingCard,
  };

  switch (templateId) {
    case ECardTypeTemplateId.People:
      return {
        ...base,
        name: tpl.name ?? '',
        tags: tpl.tags
          ? ([...tpl.tags] as [
              TCardTypeTag,
              TCardTypeTag,
              TCardTypeTag,
            ])
          : undefined,
        description: tpl.description,
      };
    case ECardTypeTemplateId.Pictures:
      return {
        ...base,
        tag: tpl.tag ?? cardTypeDefaultTag(),
      };
    case ECardTypeTemplateId.Places:
      return {
        ...base,
        header: tpl.header ?? '',
        tag: tpl.tag,
        address: tpl.address,
        additionalInfo: tpl.additionalInfo,
      };
    case ECardTypeTemplateId.Products:
    default:
      return {
        ...base,
        header: tpl.header ?? '',
        tag: tpl.tag,
        description: tpl.description,
        price: tpl.price,
        price2: tpl.price2,
      };
  }
}

export function normalizeCardTypePayload(
  raw: unknown,
): TCardTypeMessagePayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const templateId =
    (record.templateId as ECardTypeTemplateId) ?? ECardTypeTemplateId.Products;
  const altText = typeof record.altText === 'string' ? record.altText : '';
  const cardsRaw = Array.isArray(record.cards) ? record.cards : [];

  const cards = cardsRaw.map((item) =>
    normalizeCard(item, templateId),
  ) as TCardTypeCard[];

  return {
    altText,
    templateId,
    cards: cards.length > 0 ? cards : [createDefaultCard(templateId)],
  };
}

function getCardActionUrl(action: Record<string, unknown>): string {
  if (typeof action.url === 'string') return action.url;
  if (typeof action.uri === 'string') return action.uri;
  return '';
}

/**
 * 保存payloadの価格項目を読み取る。
 * 旧payloadのpriceにはlabelが無いため、欠けた項目は既定値で補完する。
 * @param raw 保存payloadのprice / price2
 * @param fallback 欠損した項目に使う既定値
 * @returns 正規化された価格項目
 */
function normalizePrice(raw: unknown, fallback: TCardTypePrice): TCardTypePrice {
  if (!raw || typeof raw !== 'object') return { ...fallback };
  const price = raw as Record<string, unknown>;

  return {
    enabled: typeof price.enabled === 'boolean' ? price.enabled : fallback.enabled,
    label: typeof price.label === 'string' ? price.label : fallback.label,
    currency:
      typeof price.currency === 'string' ? price.currency : fallback.currency,
    value: typeof price.value === 'string' ? price.value : fallback.value,
  };
}

function normalizeCard(raw: unknown, templateId: ECardTypeTemplateId): TCardTypeCard {
  if (!raw || typeof raw !== 'object') {
    return createDefaultCard(templateId);
  }
  const c = raw as Record<string, unknown>;
  const fresh = createDefaultCard(
    templateId,
    Boolean(c.isClosingCard),
  );

  const legacyTitle = typeof c.title === 'string' ? c.title : '';
  const legacyText = typeof c.text === 'string' ? c.text : '';

  if (templateId === ECardTypeTemplateId.People) {
    fresh.name = typeof c.name === 'string' ? c.name : legacyTitle;
  } else {
    fresh.header = typeof c.header === 'string' ? c.header : legacyTitle;
  }

  fresh.imageUrl =
    typeof c.imageUrl === 'string' ? c.imageUrl : fresh.imageUrl;
  if (c.images && typeof c.images === 'object') {
    const imgs = c.images as Record<string, unknown>;
    fresh.images = {
      a: typeof imgs.a === 'string' ? imgs.a : fresh.images?.a,
      b: typeof imgs.b === 'string' ? imgs.b : fresh.images?.b,
      c: typeof imgs.c === 'string' ? imgs.c : fresh.images?.c,
    };
  }
  if (typeof c.imageCount === 'number' && [1, 2, 3].includes(c.imageCount)) {
    fresh.imageCount = c.imageCount as 1 | 2 | 3;
  }

  if (c.tag && typeof c.tag === 'object') {
    fresh.tag = { ...fresh.tag!, ...(c.tag as TCardTypeTag) };
  }
  if (Array.isArray(c.tags) && fresh.tags) {
    fresh.tags = c.tags.map((t, i) => ({
      ...fresh.tags![i],
      ...(t as TCardTypeTag),
    })) as [TCardTypeTag, TCardTypeTag, TCardTypeTag];
  }

  if (c.description && typeof c.description === 'object') {
    fresh.description = {
      ...(fresh.description ?? { enabled: false, text: '' }),
      ...(c.description as { enabled: boolean; text: string }),
    };
  } else if (legacyText && fresh.description) {
    fresh.description = { enabled: true, text: legacyText };
  }

  if (c.price && typeof c.price === 'object') {
    fresh.price = normalizePrice(c.price, fresh.price ?? cardTypeDefaultPrice());
  }

  if (c.price2 && typeof c.price2 === 'object') {
    fresh.price2 = normalizePrice(
      c.price2,
      fresh.price2 ?? cardTypeDefaultPrice2(),
    );
  }

  if (c.address && typeof c.address === 'object') {
    fresh.address = {
      ...(fresh.address ?? { enabled: false, text: '' }),
      ...(c.address as { enabled: boolean; text: string }),
    };
  }

  if (c.additionalInfo && typeof c.additionalInfo === 'object') {
    const raw = c.additionalInfo as {
      enabled: boolean;
      infoType: string;
      text: string;
    };
    fresh.additionalInfo = {
      ...(fresh.additionalInfo ?? {
        enabled: false,
        infoType: 'Hours',
        text: '',
      }),
      ...raw,
      infoType: normalizeAdditionalInfoType(raw.infoType),
    };
  }

  if (Array.isArray(c.actions)) {
    fresh.actions = c.actions.slice(0, 2).map((a, index) => {
      const action = a as Record<string, unknown>;
      const label = typeof action.label === 'string' ? action.label : '';
      const url = getCardActionUrl(action);
      const text = typeof action.text === 'string' ? action.text : '';
      const actionType =
        action.actionType === ECardTypeActionType.Text ||
        action.actionType === ECardTypeActionType.Uri
          ? action.actionType
          : url
            ? ECardTypeActionType.Uri
            : text
              ? ECardTypeActionType.Text
              : '';
      const enabled =
        typeof action.enabled === 'boolean'
          ? action.enabled
          : index === 0;
      return {
        enabled,
        label: label || (index === 1 ? cardTypeDefaultAction2().label : ''),
        actionType,
        uri: url,
        text,
      };
    });
    while (fresh.actions.length < 2) {
      fresh.actions.push(
        fresh.actions.length === 0
          ? cardTypeDefaultAction()
          : cardTypeDefaultAction2(),
      );
    }
  }

  return syncCardPrimaryImage(ensureCardActions(fresh));
}

export function cardToLegacyFields(card: TCardTypeCard): {
  title: string;
  text: string;
  imageUrl?: string;
  actions: Array<{ label: string; url?: string; text?: string }>;
} {
  const title =
    card.name?.trim() ||
    card.header?.trim() ||
    card.title?.trim() ||
    'カード';

  const textParts: string[] = [];
  if (card.description?.enabled && card.description.text.trim()) {
    textParts.push(card.description.text.trim());
  }
  if (card.address?.enabled && card.address.text.trim()) {
    textParts.push(card.address.text.trim());
  }
  if (card.additionalInfo?.enabled && card.additionalInfo.text.trim()) {
    const type = card.additionalInfo.infoType || 'Info';
    textParts.push(`${type}: ${card.additionalInfo.text.trim()}`);
  }
  for (const price of [card.price, card.price2]) {
    if (!price?.enabled || !price.value.trim()) continue;
    const amount = `${price.currency}${price.value.trim()}`;
    const label = (price.label ?? '').trim();
    textParts.push(label ? `${label} ${amount}` : amount);
  }
  if (card.text?.trim()) textParts.push(card.text.trim());

  const actions: Array<{ label: string; url?: string; text?: string }> = [];
  for (const a of card.actions) {
    if (!a.enabled || !a.label.trim()) continue;
    if (a.actionType === ECardTypeActionType.Uri && a.uri?.trim()) {
      actions.push({
        label: a.label.trim(),
        url: normalizeCardTypeActionUri(a.uri) ?? a.uri.trim(),
      });
    } else if (a.actionType === ECardTypeActionType.Text && a.text?.trim()) {
      actions.push({ label: a.label.trim(), text: a.text.trim() });
    }
  }

  return {
    title,
    text: textParts.join('\n'),
    imageUrl: getCardImages(card).a || undefined,
    actions,
  };
}

export function buildCardTypeSavePayload(
  templateId: ECardTypeTemplateId,
  altText: string,
  cards: TCardTypeCard[],
): TCardTypeMessagePayload {
  const supportsMultiImage =
    templateId === ECardTypeTemplateId.Products ||
    templateId === ECardTypeTemplateId.Places;

  return {
    altText: altText.trim(),
    templateId,
    cards: cards.map((card) => {
      const imageCount = card.imageCount ?? 1;
      const images = getCardImages(card);
      const synced = supportsMultiImage
        ? {
            ...card,
            imageCount,
            images: {
              a: images.a,
              b: imageCount >= 2 ? images.b : '',
              c: imageCount >= 3 ? images.c : '',
            },
            imageUrl: images.a || undefined,
          }
        : syncCardPrimaryImage(card);
      const actions = ensureCardActions(synced).actions.map((action) =>
        action.actionType === ECardTypeActionType.Uri && action.uri
          ? {
              ...action,
              uri:
                normalizeCardTypeActionUri(action.uri) ?? action.uri.trim(),
            }
          : action,
      );
      const normalized = { ...synced, actions };
      const legacy = cardToLegacyFields(normalized);
      return {
        ...normalized,
        title: legacy.title,
        text: legacy.text,
        imageUrl: legacy.imageUrl,
      };
    }),
  };
}

export async function uploadCardImage(
  file: File,
  contentType: TCardTypeImageMimeType,
  organizationId?: number | null,
): Promise<string> {
  const extension = contentType === 'image/jpeg' ? 'jpg' : 'png';
  const signed = await getSignUrl(
    {
      filename: `card-type/${uuidv4()}.${extension}`,
      contentType,
      size: file.size,
    },
    organizationId,
  );
  await uploadFile(signed.preSignedUrl, file, contentType);
  return signed.uploadedUrl;
}
